import type { Message, ResumeEntry } from "@ag-ui/client";
import type { ConversationConnect } from "~/services/conversations";
import type { ToolCallEntry, Turn } from "./useTurns";

// What the server says the conversation is waiting on, in AG-UI's two channels:
// external tool calls answered with tool messages, and approval interrupts
// answered through `resume`.
export type PendingInput = Pick<
  ConversationConnect,
  "pending_tool_call_ids" | "pending_interrupts"
>;

export type AnswerChannel = "tool" | "interrupt";

export const NO_PENDING_INPUT: PendingInput = { pending_tool_call_ids: [], pending_interrupts: [] };

// A backend that predates pending state omits both fields.
export function pendingInputOf(connect: Partial<PendingInput>): PendingInput {
  return {
    pending_tool_call_ids: connect.pending_tool_call_ids ?? [],
    pending_interrupts: connect.pending_interrupts ?? [],
  };
}

// Un-resulted tool calls on settled turns: a pause, or an interrupted run —
// which is why `Chat` reconciles against `/connect` whenever one remains. A run
// still going, cancelled, or failed will never come back for an answer.
export function danglingToolCalls(turns: readonly Turn[]): ToolCallEntry[] {
  const calls: ToolCallEntry[] = [];
  for (const turn of turns) {
    if (turn.kind !== "assistant" || turn.isRunning || turn.cancelled || turn.failed) continue;
    calls.push(...turn.toolCalls.filter((tc) => tc.result === undefined));
  }
  return calls;
}

// Every answer is staged as a local tool message on its call, whichever channel
// carries it to the server; that message is what turns the card into a receipt.
function stagedAnswer(messages: readonly Message[], toolCallId: string): Message | undefined {
  return messages.findLast((m) => m.role === "tool" && m.toolCallId === toolCallId);
}

// The pending calls still without an answer, each with the channel that answers
// it. `pending` is the last /connect snapshot; a call drops out once it has a
// result, staged or streamed, and returns if a failed resume rolls its staged
// answer back. One response can pause several cards and the server resumes the
// batch as a whole, so the run is held until this is empty.
export function awaitingChannels(
  pending: PendingInput,
  messages: readonly Message[]
): ReadonlyMap<string, AnswerChannel> {
  const channels = new Map<string, AnswerChannel>();
  for (const id of pending.pending_tool_call_ids) channels.set(id, "tool");
  for (const interrupt of pending.pending_interrupts) {
    if (interrupt.toolCallId) channels.set(interrupt.toolCallId, "interrupt");
  }
  for (const id of channels.keys()) {
    if (stagedAnswer(messages, id)) channels.delete(id);
  }
  return channels;
}

// The approval channel's half of the batch, read off the staged answers. The
// literal "approved" approves; anything else denies with that text as the reason.
export function resumeEntries(pending: PendingInput, messages: readonly Message[]): ResumeEntry[] {
  return pending.pending_interrupts.flatMap((interrupt) => {
    const answer = interrupt.toolCallId ? stagedAnswer(messages, interrupt.toolCallId) : undefined;
    if (answer?.role !== "tool" || typeof answer.content !== "string") return [];
    const approved = answer.content === "approved";
    return [
      {
        interruptId: interrupt.id,
        status: "resolved" as const,
        payload: approved ? { approved } : { approved, reason: answer.content },
      },
    ];
  });
}
